import { useState } from "react";
import { colorService } from "../../../Services/color.service";
import { vehicleGroupService } from "../../../Services/vehicle.group.service";
import { configsService } from "../../../Services/configs.service";
import {
  FREQUENCIA,
  TIPO_EXCEL,
} from "../../../components/ReportConfig/ReportConfigList";
import { normalizeEmailList } from "../../../components/ReportConfig/vehicleGroupReportConfig.utils";

/**
 * Gerencia o envio de todas as configurações do grupo de veículos.
 *
 * @param {object} params
 * @param {object} params.group           - Objeto do grupo (vehicleGroupId, etc.)
 * @param {string|number} params.selectedId - Id da cor selecionada
 * @param {object} params.movementForm    - Estado do formulário de horário de uso
 * @param {object} params.speedForm       - Estado do formulário de velocidade
 * @param {boolean} params.visitFlag      - Flag de visita
 * @param {boolean} params.fenceFlag      - Flag de cerca eletrônica
 * @param {boolean} params.riskAreaFlag   - Flag de área de risco
 * @param {boolean} params.strechesFlag   - Flag de trechos
 * @param {object} params.archives        - Formatos de arquivo por relatório legado
 * @param {Array}  params.reportItems     - Lista de itens de relatório (VEHICLE_GROUP_REPORT_ITEMS)
 * @param {Array}  params.reportConfigPayload - Payload de configuração por relatório
 * @param {Array}  params.REPORT_TYPES    - Tipos de relatório (chave + codeReport)
 */
export function useVehicleConfigSubmit({
  group,
  selectedId,
  movementForm,
  speedForm,
  visitFlag,
  fenceFlag,
  riskAreaFlag,
  strechesFlag,
  archives,
  reportItems,
  reportConfigPayload,
  REPORT_TYPES,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [toastParams, setToastParams] = useState("");

  const getReportConfigById = (reportId) =>
    reportConfigPayload.find((item) => item.id === reportId) || {};

  const onSubmit = async () => {
    const getFlag = (value) => (value ? "S" : "N");

    setIsSaving(true);
    try {
      const responseColors = await colorService.sendColor(
        Number(selectedId),
        group.vehicleGroupId
      );

      const payloadMovement = {
        checkMovWeekdaysFlag: movementForm.weekdays.flag ? "S" : "N",
        checkMovWeekdaysStart: movementForm.weekdays.start,
        checkMovWeekdaysEnd: movementForm.weekdays.end,

        checkMovSaturdayFlag: movementForm.saturday.flag ? "S" : "N",
        checkMovSaturdayStart: movementForm.saturday.start,
        checkMovSaturdayEnd: movementForm.saturday.end,

        checkMovSundayFlag: movementForm.sunday.flag ? "S" : "N",
        checkMovSundayStart: movementForm.sunday.start,
        checkMovSundayEnd: movementForm.sunday.end,

        emailNotificationFlag: movementForm.emailNotificationFlag ? "S" : "N",
        attachmentFormat: movementForm.attachmentFormat,
      };
      const responseMovement = await vehicleGroupService.sendMovement(
        group.vehicleGroupId,
        payloadMovement
      );

      const payloadSpeed = {
        urbanSpeedCheck: speedForm.urban.flag ? "S" : "N",
        urbanSpeedValue: speedForm.urban.value,

        highwaySpeedCheck: speedForm.highway.flag ? "S" : "N",
        highwaySpeedValue: speedForm.highway.value,

        speedEmailCheck: speedForm.emailNotificationFlag ? "S" : "N",
        speedAttachmentFormatCheck: speedForm.attachmentFormat,
      };
      const responseSpeed = await vehicleGroupService.sendSpeed(
        group.vehicleGroupId,
        payloadSpeed
      );

      const payloadKmz = {
        kmzEmailCheck: getFlag(visitFlag),
      };
      const responseKmz = await configsService.sendKmz(
        group.vehicleGroupId,
        payloadKmz
      );

      const payloadVisit = {
        visitEmailCheck: getFlag(visitFlag),
        visitAttachmentFormatCheck: archives.archivePontoDeVista,
      };
      const responseVisit = await configsService.sendVisit(
        group.vehicleGroupId,
        payloadVisit
      );

      const payloadFence = {
        fenceEmailCheck: getFlag(fenceFlag),
        fenceAttachmentFormatCheck: archives.archiveCercaEletronica,
      };
      const responseFence = await configsService.sendFence(
        group.vehicleGroupId,
        payloadFence
      );

      const payloadRiskArea = {
        riskAreaEmailCheck: getFlag(riskAreaFlag),
        riskAreaAttachmentFormatCheck: archives.archiveAreaDeRisco,
      };
      const responseRiskArea = await configsService.sendRiskArea(
        group.vehicleGroupId,
        payloadRiskArea
      );

      const payloadStreches = {
        stretchEmailCheck: getFlag(strechesFlag),
        stretchAttachmentCheck: archives.archiveMovimentacao,
      };
      const responseStreches = await configsService.sendStreches(
        group.vehicleGroupId,
        payloadStreches
      );

      // Handle high-speed summary separately via dedicated endpoint
      const handleHighSpeedFor = async (reportKey) => {
        // Re-fetch emails from server to ensure up-to-date list
        const reportTypeObj = REPORT_TYPES.find((t) => t.key === reportKey);
        if (!reportTypeObj) return true;

        const fetched = await configsService.fetchEmailConfiguration(
          group.vehicleGroupId,
          reportTypeObj.codeReport
        );
        const emails = normalizeEmailList(fetched).map((e) => e.email).filter(Boolean);
        if (emails.length === 0) return true;

        const currentConfig = getReportConfigById(reportKey);
        const payload = {
          vehicleGroupId: group.vehicleGroupId,
          reportCode: reportTypeObj.codeReport,
          speed: Number(currentConfig.speedHigh ?? 30),
          type: Number(currentConfig.typeHigh ?? 1),
        };

        const response = await vehicleGroupService.sendHighSpeedConfiguration(payload);
        return response !== false;
      };

      const responseHighSpeedPdf = await handleHighSpeedFor("speedSummaryPdfPeriod");
      const responseHighSpeedXls = await handleHighSpeedFor("speedSummaryXlsPeriod");

      const responseReportsPdf = await (async () => {
        const relevantItems = reportItems.filter((item) => item.tipo === "PDF");
        const responses = [];

        for (const itemReport of relevantItems) {
          const config = getReportConfigById(itemReport.id);

          const fetched = await configsService.fetchEmailConfiguration(
            group.vehicleGroupId,
            itemReport.codeReport
          );
          const emails = normalizeEmailList(fetched).map((e) => e.email).filter(Boolean);

          if (emails.length === 0) continue;

          const frequency = config?.frequencia === FREQUENCIA.SEMANAL ? "S" : "D";
          const basePayload = {
            VehicleGroupId: group?.vehicleGroupId,
            CodeReport: itemReport.codeReport,
            CheckEmail: "S",
            Frequency: frequency,
          };

          for (const email of emails) {
            responses.push(
              await vehicleGroupService.sendReportsConfigurationPdf({
                ...basePayload,
                Email: email,
              })
            );
          }
        }

        if (responses.length === 0) return true;
        return responses.every((response) => response !== false);
      })();

      const responseReportsXls = await (async () => {
        const relevantItems = reportItems.filter((item) => item.tipo === "EXCEL");
        const responses = [];

        for (const itemReport of relevantItems) {
          const config = getReportConfigById(itemReport.id);

          const fetched = await configsService.fetchEmailConfiguration(
            group.vehicleGroupId,
            itemReport.codeReport
          );
          const emails = normalizeEmailList(fetched).map((e) => e.email).filter(Boolean);

          if (emails.length === 0) continue;

          const frequency = config?.frequencia === FREQUENCIA.SEMANAL ? "S" : "D";
          const separateSheets = config?.tipoExcel === TIPO_EXCEL.ABAS_SEPARADAS ? "S" : "N";
          const basePayload = {
            VehicleGroupId: group?.vehicleGroupId,
            CodeReport: itemReport.codeReport,
            CheckEmail: "S",
            Frequency: frequency,
            SeparateSheets: separateSheets,
          };

          for (const email of emails) {
            responses.push(
              await vehicleGroupService.sendReportsConfigurationXls({
                ...basePayload,
                Email: email,
              })
            );
          }
        }

        if (responses.length === 0) return true;
        return responses.every((response) => response !== false);
      })();

      if (
        responseColors &&
        responseSpeed &&
        responseMovement &&
        responseKmz &&
        responseFence &&
        responseVisit &&
        responseRiskArea &&
        responseStreches &&
        responseReportsPdf !== false &&
        responseReportsXls !== false &&
        responseHighSpeedPdf !== false &&
        responseHighSpeedXls !== false
      ) {
        setToastParams(
          JSON.stringify({
            show: true,
            title: "Atenção!",
            body: "Configurações atualizadas",
            color: "Success",
          })
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, toastParams, setToastParams, onSubmit };
}
